import React, { useEffect, useMemo, useState } from 'react'
import { Dimensions, View, ViewProps } from 'react-native'
import { breakpoints } from '../theme'
import { composeStyle } from '../helpers'

export const BREAK_POINTS = [
  { key: 'xl', value: breakpoints.xl },
  { key: 'lg', value: breakpoints.lg },
  { key: 'md', value: breakpoints.md },
  { key: 'sm', value: breakpoints.sm },
  { key: 'xs', value: breakpoints.xs },
]

function getBreakPoint() {
  const { width } = Dimensions.get('window')
  const match = BREAK_POINTS.find((p) => p.value <= width)
  return (match && match.key) || BREAK_POINTS[BREAK_POINTS.length - 1].key
}

export function useBreakPoint() {
  const [point, setPoint] = useState(getBreakPoint())
  useEffect(() => {
    const onChange = () => setPoint(getBreakPoint())
    const a = Dimensions.addEventListener('change', onChange)
    return a && a.remove
  }, [])
  return point
}

const isValid = (value) => isFinite(value) && value >= 0

function useBreakPointValue(point, values) {
  const { xs, sm, md, lg, xl } = values
  return useMemo(
    () =>
      ({
        xl: [xl, lg, md, sm, xs],
        lg: [lg, md, sm, xs],
        md: [md, sm, xs],
        sm: [sm, xs],
        xs: [xs],
      }[point]?.find(isValid) || 0),
    [point, xs, sm, md, lg, xl],
  )
}

export const Row = ({ children, style, onLayout }: ViewProps) => {
  const point = useBreakPoint()
  const marginHorizontal = -useBreakPointValue(point, { xs: 5, sm: 10 })
  return (
    <View
      style={composeStyle(
        { flexDirection: 'row', flexWrap: 'wrap', marginHorizontal },
        style,
      )}
      onLayout={onLayout}
    >
      {children}
    </View>
  )
}

const SIZES = {
  1: (100 / 12).toFixed(6) + '%',
  2: ((100 / 12) * 2).toFixed(6) + '%',
  3: ((100 / 12) * 3).toFixed(6) + '%',
  4: ((100 / 12) * 4).toFixed(6) + '%',
  5: ((100 / 12) * 5).toFixed(6) + '%',
  6: ((100 / 12) * 6).toFixed(6) + '%',
  7: ((100 / 12) * 7).toFixed(6) + '%',
  8: ((100 / 12) * 8).toFixed(6) + '%',
  9: ((100 / 12) * 9).toFixed(6) + '%',
  10: ((100 / 12) * 10).toFixed(6) + '%',
  11: ((100 / 12) * 11).toFixed(6) + '%',
  12: ((100 / 12) * 12).toFixed(6) + '%',
}

interface ColProps extends ViewProps {
  xs?: number
  sm?: number
  md?: number
  lg?: number
  xl?: number
}

export const Col = ({ children, style, ...rest }: ColProps) => {
  const point = useBreakPoint()
  const paddingHorizontal = useBreakPointValue(point, { xs: 5, sm: 10 })
  const size = useBreakPointValue(point, rest)
  const width = SIZES[size]

  return (
    <View style={composeStyle({ paddingHorizontal, width }, style)}>
      {children}
    </View>
  )
}
